import { useEffect, useState } from 'react';
import { fetchTrailForDocument, TRAIL_CHANGED_EVENT, type DocumentTrailDto } from '../../lib/rfqLifecycleApi';
import { BOX_OF_TYPE, type TrailTarget } from './trailTarget';
import type { BoxKey } from './lifecycleModel';

/** Whether the trail holds the target (so it is proven to be the same RFQ). */
function holds(trail: DocumentTrailDto, target: TrailTarget): boolean {
  const key = BOX_OF_TYPE[target.type];
  if (key === 'rfq') return trail.rfq._id === target.id;
  const docs = trail.lifecycle[key as Exclude<BoxKey, 'rfq'>];
  return !!docs && docs.some((d) => d.id === target.id);
}

/**
 * The trail of the RFQ that holds the open document, for the sidebar. null = no trail: nothing open,
 * the document is in no RFQ's chain, the read failed, or it has not arrived yet.
 *
 * Moving between documents of one RFQ keeps the trail on screen while the next read runs (it is only
 * kept when it provably holds the new document), so the menu never flashes in between. A failed read
 * leaves the plain menu, which is exactly what the sidebar showed before the trail existed.
 */
export function useItemTrail(target: TrailTarget | null, enabled: boolean): DocumentTrailDto | null {
  const key = target && enabled ? `${target.type}:${target.id}` : '';
  const [state, setState] = useState<{ key: string; trail: DocumentTrailDto | null }>({ key: '', trail: null });
  // Bumped by TRAIL_CHANGED_EVENT: a page changed the chain without changing the URL.
  const [stamp, setStamp] = useState(0);

  useEffect(() => {
    const onChanged = () => setStamp((n) => n + 1);
    window.addEventListener(TRAIL_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(TRAIL_CHANGED_EVENT, onChanged);
  }, []);

  useEffect(() => {
    if (!key || !target) return;
    const controller = new AbortController();
    fetchTrailForDocument(target.type, target.id, { signal: controller.signal })
      .then((trail) => {
        if (!controller.signal.aborted) setState({ key, trail });
      })
      .catch((err: any) => {
        if (controller.signal.aborted || err?.name === 'AbortError') return;
        setState({ key, trail: null });
      });
    return () => controller.abort();
    // `target` is read through `key`, which changes exactly when it does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, stamp]);

  if (!key || !target || !state.trail) return null;
  return state.key === key || holds(state.trail, target) ? state.trail : null;
}
